"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

/* Content is fully visible in the server HTML. <SectionReveals /> (mounted
   once per page) hides below-the-fold [data-reveal] blocks after first paint
   and reveals them once, staggered, as they scroll in. */

type Direction = "up" | "down" | "left" | "right" | "scale" | "none";

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  /** @deprecated Stagger now comes from ScrollTrigger.batch; kept for call-site compatibility. */
  delay?: number;
  style?: React.CSSProperties;
  id?: string;
  /** @deprecated Reveals are a uniform y:16 fade. */
  direction?: Direction;
  /** @deprecated No longer applied. */
  blur?: boolean;
  /** @deprecated No longer applied. */
  distance?: number;
  /** @deprecated No longer applied. */
  duration?: number;
  /** If true, children won't render until scrolled near viewport (saves initial parse) */
  lazy?: boolean;
}

export default function ScrollReveal({
  children,
  className = "",
  style,
  id,
  lazy = false,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [hasMounted, setHasMounted] = useState(!lazy);

  useEffect(() => {
    const el = ref.current;
    if (!lazy || !el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [lazy]);

  return (
    <div ref={ref} data-reveal="" className={className} id={id} style={{ ...style, contain: "layout paint" }}>
      {/* Lazy rendering: don't mount children until near viewport */}
      {hasMounted ? children : <div style={{ height: 460, minHeight: 460 }} />}
    </div>
  );
}
